import { Box, Button, TabPanel, TabPanels } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import { useTranslation } from 'react-i18next';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { isEqual } from 'lodash';
import {
  EventGroupOrCheckpoint,
  ProjectHistoryTimeline,
} from './ProjectHistoryTimeline';
import { generateDiff } from './utils/diffing';
import { useProjectHistory } from './hooks/useProjectHistory';
import { Drawer } from 'src/components/common/Drawer';
import {
  useProjectCheckpointEventHistory,
  useProjectEventGroupHistory,
  useResolveEvent,
} from 'src/hooks/event-history';
import { useDrawer } from 'src/components/common/DrawerContext';
import {
  GetProjectDeepDocument,
  HistoryEventDeepFragment,
  ModelsDeepFragment,
} from 'src/gql/graphql';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { ModelsMap } from 'src/components/common/ForgeViewer';

type ProjectHistoryDrawerProps = {
  projectId: string;
};

type ResolvedProjectState = unknown;

function getEventToResolve(
  item: EventGroupOrCheckpoint
): HistoryEventDeepFragment | null {
  if ('firstEvent' in item) {
    return item.firstEvent ?? null;
  }
  return item as HistoryEventDeepFragment;
}

export const ProjectHistoryDrawer = ({
  projectId,
}: ProjectHistoryDrawerProps) => {
  const {
    selectedRange,
    tempModelOverlaysRef,
    setSelectedRange,
    setIsHistoryDrawerOpen,
    setDiff,
    setReferenceState,
    setEventsActiveModels,
    setResolvedStates,
  } = useProjectHistory();
  const {
    eventGroups,
    hasNextPage,
    reloadEventGroups,
    loadMoreGroups,
    isFetchingGroups,
  } = useProjectEventGroupHistory(projectId);
  const {
    checkpointEvents,
    reloadCheckpointEvents,
    isFetchingGroups: isFetchingCheckpointEvents,
  } = useProjectCheckpointEventHistory(projectId);
  const {
    getResolvedStateOfEvent,
    unloadOverlayModels,
    unloadModels,
    resolveCurrentProjectLazily,
    clearColors,
  } = useResolveEvent(projectId);
  const { isDrawerOpen } = useDrawer();
  const [eventsBusy, setEventsBusy] = useState(false);
  const [debouncedBusy, setDebouncedBusy] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [modelsToUnload, setModelsToUnload] = useState<
    Array<Autodesk.Viewing.Model>
  >([]);

  const { t } = useTranslation('project-history');

  // Debounce the disabled state to avoid brief flickers
  useEffect(() => {
    if (eventsBusy) {
      const id = setTimeout(() => setDebouncedBusy(true), 150);
      return () => clearTimeout(id);
    }
    setDebouncedBusy(false);
  }, [eventsBusy]);

  useEffect(() => {
    setIsHistoryDrawerOpen(isDrawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen]);

  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: {
      id: projectId,
    },
  });

  const applyResolvedStates = useCallback(
    (resolvedStates: ResolvedProjectState[]) => {
      const eventsActiveModels: ModelsDeepFragment[] = [];
      const seenSet = new Set<string>();
      const eventsLoadedModelUrns = new Set<string>();

      resolvedStates.forEach((stateItem) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = stateItem as any;
        s.propertyContext.loadedModels.forEach((urn: string) =>
          eventsLoadedModelUrns.add(urn)
        );
        if (s.activeModels?.length) {
          s.activeModels.forEach((activeModel: ModelsDeepFragment) => {
            const seen = seenSet.has(activeModel.id);
            if (!seen) {
              eventsActiveModels.push(activeModel);
              seenSet.add(activeModel.id);
            }
          });
        }
      });

      const modelsToUnload: Array<Autodesk.Viewing.Model> = [];
      const newTempOverlays: ModelsMap = {};
      for (const tempModelUrn of Object.keys(tempModelOverlaysRef.current)) {
        if (!eventsLoadedModelUrns.has(tempModelUrn)) {
          modelsToUnload.push(tempModelOverlaysRef.current[tempModelUrn]);
        } else {
          newTempOverlays[tempModelUrn] =
            tempModelOverlaysRef.current[tempModelUrn];
        }
      }
      tempModelOverlaysRef.current = newTempOverlays;
      if (modelsToUnload.length) {
        setModelsToUnload(modelsToUnload);
      }

      setEventsActiveModels((prevModels) => {
        if (isEqual(prevModels, eventsActiveModels)) return prevModels;
        return eventsActiveModels;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Handle single checkpoint selection - show the checkpoint's state with an empty diff.
   * We use an empty diff {} instead of null so that UI components still apply the historical state.
   */
  const resolveSingleCheckpoint = useCallback(
    async (items: EventGroupOrCheckpoint[], baseIdx: number) => {
      const checkpoint = items[baseIdx];
      const event = getEventToResolve(checkpoint);
      if (!event) return false;
      const state = await getResolvedStateOfEvent(event);
      applyResolvedStates([state, state]);
      // Use empty diff {} instead of null - UI components check for truthy diff
      setDiff({});
      setReferenceState(state);
      setResolvedStates([state, state]);
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applyResolvedStates, getResolvedStateOfEvent]
  );

  /**
   * Resolve current project state (for "Current Version" selection).
   */
  const resolveCurrentVersion = useCallback(async () => {
    if (!projectData?.project) return;
    const state = await resolveCurrentProjectLazily(projectData.project);
    applyResolvedStates([state, state]);
    setDiff({});
    setReferenceState(state);
    setResolvedStates([state, state]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyResolvedStates, projectData?.project, resolveCurrentProjectLazily]);

  /**
   * Convert display index to items array index, accounting for header offset.
   */
  const toItemIndex = (displayIndex: number, hasHeader: boolean): number => {
    return hasHeader ? displayIndex - 1 : displayIndex;
  };

  /**
   * Resolve events/checkpoints for a given range.
   * Works for both event groups (Events tab) and checkpoint events (Checkpoints tab).
   */
  const resolveForRange = useCallback(
    // eslint-disable-next-line complexity
    async (
      range: [number, number],
      items: EventGroupOrCheckpoint[],
      hasCurrentVersionHeader: boolean
    ) => {
      try {
        clearColors();
        if (items.length <= 0) return;
        setEventsBusy(true);

        const [baseIdx, secondaryIdx] = range;
        const isSingleSelection = secondaryIdx - baseIdx === 1;
        const isCurrentVersionSelected =
          hasCurrentVersionHeader && baseIdx === 0;

        // Check if items are checkpoint events (not groups with firstEvent)
        const areItemsCheckpoints =
          items.length > 0 &&
          items[0].isCheckpoint &&
          !('firstEvent' in items[0]);

        // Case 1: Current version only selected → load current state
        if (isCurrentVersionSelected && isSingleSelection) {
          await resolveCurrentVersion();
          return;
        }

        // Case 2: Single checkpoint selected (Checkpoints tab only) → show state, no diff
        if (isSingleSelection && areItemsCheckpoints) {
          const itemIdx = toItemIndex(baseIdx, hasCurrentVersionHeader);
          await resolveSingleCheckpoint(items, itemIdx);
          return;
        }

        // Case 3: Multi-selection → diff between two states
        //
        // For EVENTS tab:
        //   Each event's state = state BEFORE that event occurred
        //   To see what changed: compare event state with the state from the event AFTER it
        //   AFTER state: baseIdx - 1 (or current version if that would be the header)
        //   BEFORE state: secondaryIdx - 1
        //
        // For CHECKPOINTS tab:
        //   Each checkpoint's state = state AT that checkpoint
        //   To compare checkpoints: directly compare the selected checkpoints
        //   AFTER state: baseIdx (the newer selected checkpoint)
        //   BEFORE state: secondaryIdx - 1 (the older selected checkpoint)

        const resolvePromises: Promise<ResolvedProjectState>[] = [];

        if (areItemsCheckpoints) {
          // CHECKPOINTS TAB: Compare checkpoint states directly
          const baseItemIdx = toItemIndex(baseIdx, hasCurrentVersionHeader);
          const secondaryItemIdx = toItemIndex(
            secondaryIdx - 1,
            hasCurrentVersionHeader
          );

          // If secondary is out of bounds, show single state
          // (skip baseItemIdx check if current version is selected, since we won't use it)
          const isBaseOutOfBounds =
            !isCurrentVersionSelected &&
            (baseItemIdx >= items.length || baseItemIdx < 0);
          const isSecondaryOutOfBounds =
            secondaryItemIdx >= items.length || secondaryItemIdx < 0;

          if (isBaseOutOfBounds || isSecondaryOutOfBounds) {
            const itemIdx = toItemIndex(baseIdx, hasCurrentVersionHeader);
            await resolveSingleCheckpoint(items, Math.max(0, itemIdx));
            return;
          }

          // Use current version if header is selected as base
          if (isCurrentVersionSelected) {
            if (!projectData?.project) return;
            resolvePromises.push(
              resolveCurrentProjectLazily(projectData.project)
            );
          } else {
            const baseEvent = getEventToResolve(items[baseItemIdx]);
            if (!baseEvent) return;
            resolvePromises.push(getResolvedStateOfEvent(baseEvent));
          }

          const secondaryEvent = getEventToResolve(items[secondaryItemIdx]);
          if (!secondaryEvent) return;
          resolvePromises.push(getResolvedStateOfEvent(secondaryEvent));
        } else {
          // EVENTS TAB: Use offset logic (event state = state BEFORE event)
          const secondaryItemIdx = toItemIndex(
            secondaryIdx - 1,
            hasCurrentVersionHeader
          );

          // If secondary is out of bounds, just show single state
          if (secondaryItemIdx >= items.length || secondaryItemIdx < 0) {
            const itemIdx = toItemIndex(baseIdx, hasCurrentVersionHeader);
            await resolveSingleCheckpoint(items, itemIdx);
            return;
          }

          // AFTER state: current version if baseIdx - 1 would be the header (or before it)
          const useCurrentVersionAsAfter =
            baseIdx === 0 || (hasCurrentVersionHeader && baseIdx === 1);

          if (useCurrentVersionAsAfter) {
            // Base is current version (state after all selected events)
            if (!projectData?.project) return;
            resolvePromises.push(
              resolveCurrentProjectLazily(projectData.project)
            );
          } else {
            // Base is the event after the selection (state after selected events)
            const baseItemIdx = toItemIndex(
              baseIdx - 1,
              hasCurrentVersionHeader
            );
            const event = getEventToResolve(items[baseItemIdx]);
            if (!event) return;
            resolvePromises.push(getResolvedStateOfEvent(event));
          }

          // BEFORE state: the selected event's state (state before the selected event(s))
          const secondaryEvent = getEventToResolve(items[secondaryItemIdx]);
          if (!secondaryEvent) return;
          resolvePromises.push(getResolvedStateOfEvent(secondaryEvent));
        }

        const resolvedStates = await Promise.all(resolvePromises.reverse());
        applyResolvedStates(resolvedStates);
        const diff = generateDiff(
          resolvedStates as [ResolvedProjectState, ResolvedProjectState]
        );
        setDiff(diff);
        setReferenceState(resolvedStates[0]);
        setResolvedStates(
          resolvedStates as [ResolvedProjectState, ResolvedProjectState]
        );
      } catch (err) {
        console.error('Failed to resolve for range', range, err);
      } finally {
        setEventsBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applyResolvedStates,
      clearColors,
      projectData?.project,
      getResolvedStateOfEvent,
      resolveCurrentProjectLazily,
    ]
  );

  const rangeChangeHandler = useCallback(
    (range: [number, number]) => {
      setSelectedRange(range);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // this handles unloading models when navigating between events
  useEffect(() => {
    if (modelsToUnload.length > 0) {
      unloadModels(modelsToUnload);
      setModelsToUnload([]);
    }
  }, [modelsToUnload, unloadModels]);

  // Listen to the state of the drawer and unload the models that are are currently loaded
  useEffect(() => {
    // If model is closed, unload any overlays added by the event history components
    if (!isDrawerOpen) {
      // This handles unloading models when the modal closes
      unloadOverlayModels();
      clearColors();
      // Reset the state when drawer is closed
      // Default to [0, 2] to show diff between current version and latest action
      setSelectedRange([0, 2]);
      setDiff(null);
      setReferenceState(null);
      setEventsActiveModels(null);
      setResolvedStates(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearColors, isDrawerOpen, unloadOverlayModels]);

  // Resolve when drawer opens or selection changes
  useEffect(() => {
    if (!isDrawerOpen) return;

    // Events tab: show header unless first event is a checkpoint
    // Checkpoints tab: always show header (current version is always available)
    const hasCurrentVersionHeader =
      activeTabIndex === 0 ? !eventGroups[0]?.isCheckpoint : true; // Checkpoints tab always has header

    // Reload fresh data then resolve
    const reload =
      activeTabIndex === 0 ? reloadEventGroups : reloadCheckpointEvents;
    reload().then((freshItems) => {
      resolveForRange(selectedRange, freshItems, hasCurrentVersionHeader);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, activeTabIndex, selectedRange]);

  const shouldShowCurrentVersionHeader = useMemo(() => {
    return activeTabIndex === 0 && !eventGroups[0]?.isCheckpoint;
  }, [activeTabIndex, eventGroups]);

  return (
    <Drawer
      id="project-history-drawer"
      title={[
        t('project-history:timeline.title'),
        t('project-history:timeline.checkpoints'),
      ]}
      onTabChange={setActiveTabIndex}
    >
      <Box position="relative">
        <TabPanels>
          <TabPanel>
            <ProjectHistoryTimeline
              key={isDrawerOpen ? 'open' : 'closed'}
              isDisabled={debouncedBusy}
              isLoading={isFetchingGroups && eventGroups.length === 0}
              eventGroups={eventGroups}
              initialSelectedRange={[0, 2]}
              onSelectionChange={rangeChangeHandler}
              projectId={projectId}
              setIsLoading={setEventsBusy}
              reloadEventGroups={reloadEventGroups}
              shouldShowCurrentVersionHeader={shouldShowCurrentVersionHeader}
            />
            {hasNextPage && (
              <Button
                isDisabled={eventsBusy}
                isLoading={isFetchingGroups}
                onClick={loadMoreGroups}
                size="sm"
                variant="ghost"
                leftIcon={<SparkelIcon icon={faArrowDown} color={'inherit'} />}
              >
                {t('load-more-events')}
              </Button>
            )}
          </TabPanel>
          <TabPanel>
            <ProjectHistoryTimeline
              key={isDrawerOpen ? 'open' : 'closed'}
              isDisabled={debouncedBusy}
              isLoading={
                isFetchingCheckpointEvents && checkpointEvents.length === 0
              }
              eventGroups={checkpointEvents}
              initialSelectedRange={[0, 2]}
              onSelectionChange={rangeChangeHandler}
              projectId={projectId}
              setIsLoading={setEventsBusy}
              reloadEventGroups={reloadCheckpointEvents}
              shouldShowCurrentVersionHeader={true}
            />
          </TabPanel>
        </TabPanels>
      </Box>
    </Drawer>
  );
};
